import type { BossAttackTimeline } from './boss_pose_timeline'
import type { BossPoseMotion } from './boss_pose_timeline'

const smooth = (value: number): number => {
  const p = Math.max(0, Math.min(1, value))
  return p * p * (3 - 2 * p)
}

/** Approved review clock: 850–900ms hold, then 35ms release; no body scaling. */
export function cannonFajinPressure(ms: number): { pressure: number; rot: number; scale: number } {
  if (!Number.isFinite(ms) || ms < 90 || ms >= 935) return { pressure: 0, rot: 0, scale: 1 }
  const time = Math.min(ms, 850)
  const pressure = smooth((time - 180) / 670)
  const release = 1 - smooth((ms - 900) / 35)
  const tremor = Math.sin((time - 650) / 1000 * Math.PI * 2 * 24) * .38
    * smooth((time - 650) / 50) * (1 - smooth((time - 815) / 35))
  return { pressure, rot: (1.6 * pressure + tremor) * release, scale: 1 }
}

/** Convert the review's rotate(tension) → translate → rotate(-timeline) to sprite coordinates. */
export function cannonFajinBodyMotion(motion: BossPoseMotion, ms: number): BossPoseMotion {
  const angle = motion.rot * Math.PI / 180
  return {
    ...motion,
    rot: cannonFajinPressure(ms).rot - motion.rot,
    offsetX: Math.cos(angle) * motion.offsetX + Math.sin(angle) * motion.offsetY,
    offsetY: -Math.sin(angle) * motion.offsetX + Math.cos(angle) * motion.offsetY,
  }
}

export function isExecutionerFajin(attackId: string | null | undefined): boolean {
  return attackId === 'exec_bladeFajin' || attackId === 'exec_cannonFajin'
}

// Adopted 09-17. Raw art, prompts and registration evidence live in
// experiments/cannon-redesign-0915/. Each image includes the entire cannon.
// Anchors preserve the rear boot position; never normalize by crouched height.
export const CANNON_FAJIN_ART: Record<string, { scale: number; anchorX: number }> = {
  cannon_fajin_load: { scale: 1.03, anchorX: 583 },
  cannon_fajin_step: { scale: 1.03, anchorX: 583 },
  cannon_fajin_land: { scale: 1.03, anchorX: 583 },
  // 10-23 作者「待機にそろえる」(検収 kensyu-exec-answers-1023 Q-2): 振りと振り抜きは体が待機の 82〜83% に縮んで見えたので、
  //   待機の体の量にそろえた (旧 cut 1.03 / anchorX 1090・finish 0.98 / anchorX 982 = DEV の ?execCannonFajinSize=old)。
  //   anchorX は後ろの靴 (cut 1387・finish 1288 = 床から 50px の帯の右の塊の中点) が旧と同じ床の位置に残るように
  //   R − (R − 旧anchor) × 旧scale / 新scale で出した。tests/exec_cannon_fajin_size.test.ts が溜めの後ろの靴と照合する。
  cannon_fajin_cut: { scale: 1.26, anchorX: 1144 },
  cannon_fajin_finish: { scale: 1.18, anchorX: 1034 },
}

export const CANNON_FAJIN_PREVIOUS_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
  { phase: 'telegraph', at: 0.10, image: 'cannon_fajin_load', hold: true, tag: 'anticipation', ease: 'step' },
  // Low front-foot transport, then 80ms of planted support before the swing.
  { phase: 'telegraph', at: 680 / 900, image: 'cannon_fajin_step', hold: true, ease: 'step' },
  { phase: 'telegraph', at: 820 / 900, image: 'cannon_fajin_land', hold: true, ease: 'step' },
  { phase: 'active', at: 0, image: 'cannon_fajin_cut', hold: true, tag: 'contact', ease: 'step' },
  { phase: 'active', at: 0.35, image: 'cannon_fajin_finish', hold: true, tag: 'followThrough', ease: 'step' },
  { phase: 'recovery', at: 0, image: 'cannon_fajin_finish', hold: true, tag: 'followThrough', ease: 'step' },
  { phase: 'recovery', at: 0.60, image: 'idle', hold: true, tag: 'recover', ease: 'step' },
  { phase: 'recovery', at: 1, image: 'idle', hold: true },
]

// 09-16 feedback: isolated hand/foot changes read as separate actions.
// Keep the planted load as one whole-body anticipation, then release together.
export const CANNON_FAJIN_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
  { phase: 'telegraph', at: 0.10, image: 'cannon_fajin_load', hold: true,
    tag: 'anticipation', ease: 'easeOut', rot: 1 },
  { phase: 'telegraph', at: 0.76, image: 'cannon_fajin_load', hold: true,
    ease: 'easeInOut', rot: -1.5 },
  { phase: 'telegraph', at: 0.97, image: 'cannon_fajin_load', hold: true,
    ease: 'easeIn', rot: -1.5 },
  { phase: 'active', at: 0, image: 'cannon_fajin_cut', hold: true,
    tag: 'contact', ease: 'easeOut', offset: { x: 14 } },
  { phase: 'active', at: 0.35, image: 'cannon_fajin_finish', hold: true,
    tag: 'followThrough', ease: 'easeOut', offset: { x: 18 }, rot: 1 },
  { phase: 'recovery', at: 0, image: 'cannon_fajin_finish', hold: true,
    tag: 'followThrough', ease: 'step', offset: { x: 18 }, rot: 1 },
  { phase: 'recovery', at: 0.30, image: 'cannon_fajin_finish', hold: true,
    tag: 'recover', ease: 'easeInOut', offset: { x: 18 }, rot: 1 },
  { phase: 'recovery', at: 0.85, image: 'idle', hold: true,
    ease: 'easeOut', offset: { x: 3 } },
  { phase: 'recovery', at: 1, image: 'idle', hold: true },
]

let enabled = true
export function setCannonFajinEnabled(value: boolean): void { enabled = value }
export function cannonFajinEnabled(): boolean { return enabled }
